/**
 * federal_core.ts — the FEDERAL layer's pure rules (society wave, 2026-09-19).
 *
 * A civilization gets a standing government above its towns: an ASSEMBLY in
 * which every sitting mayor casts their settlement's votes as a BLOC, a
 * SPEAKER elected from the city mayors, six ministers, a TRIBUNAL and a
 * conserved treasury. The owner's decision (2026-09-19) fixes its reach: a
 * settlement keeps about NINE TENTHS of its freedom.
 *
 *   · SIX ENUMERATED POWERS and no others: (1) war and peace with the OTHER
 *     civilization, (2) customs on trade across the civ border and the sky lane,
 *     (3) federal works paid from federal money, (4) the relief reserve,
 *     (5) Tribunal MEDIATION of same-civ quarrels, (6) advisory standards.
 *   · The LEVY is capped at a tenth of a settlement's treasury INFLOW.
 *   · Contingents are REQUESTED, never commanded.
 *   · The Tribunal offers an off-ramp, imposes a delay and sets a price. It
 *     never forbids a war and never moves a coin without both mayors.
 *
 * Votes are DEGRESSIVE (1 / 3 / 5 / 12) and a bill needs a DOUBLE MAJORITY — of
 * bloc votes and of census population — because the live world holds 240
 * hamlets of eight people: flat Lycian 3/2/1 would give them ~60 % of each
 * Assembly on a quarter of the people.
 *
 * Everything here is deterministic and free of the database. `federal_ops.ts`
 * gathers the rows; the harness pins these rules without a connection.
 *
 * MIRROR: client/src/lib/federal_core.ts must stay byte-identical (the docket
 * shows the same weights, thresholds and stances the server applies).
 */

export type FedCiv = 'a' | 'b';

// ── Votes ────────────────────────────────────────────────────────────────────

/** Bloc votes by settlement tier. Districts are wards of a capital; the
 *  Grand Circuit is neutral ground. */
export const TIER_VOTES: Record<string, number> = { hamlet: 1, town: 3, city: 5, capital: 12, circuit: 0, district: 0 };

/** Bloc votes of a PLAYER city by rank (1 outpost … 5 metropolis). */
export const PLAYER_CITY_VOTES: readonly number[] = [0, 1, 1, 3, 5, 5];
export function playerCityVotes(rank: number): number {
  return PLAYER_CITY_VOTES[Math.max(0, Math.min(5, Math.floor(rank)))] ?? 0;
}

/** All player cities together hold at most this share of an Assembly. */
export const PLAYER_VOTE_CAP_SHARE = 0.2;

/**
 * Scale player-city votes so their sum never exceeds `PLAYER_VOTE_CAP_SHARE`
 * of the WHOLE assembly (npc + player). Returns the multiplier in (0, 1].
 * With P player votes and N npc votes the cap is P' ≤ share × (N + P'), i.e.
 * P' ≤ N × share / (1 − share).
 */
export function playerVoteScale(npcVotes: number, playerVotes: number): number {
  if (playerVotes <= 0) return 1;
  const cap = npcVotes * PLAYER_VOTE_CAP_SHARE / (1 - PLAYER_VOTE_CAP_SHARE);
  return playerVotes <= cap ? 1 : cap / playerVotes;
}

/** A resident player's federal preference weighs this many ballots inside
 *  their settlement's stance (the mayoral tally's PLAYER_PREF_WEIGHT). */
export const FED_PLAYER_PREF_WEIGHT = 8;
/** Standing a resident player needs to cast a federal preference. */
export const FED_PREF_MIN_REGARD = 40;

export interface BlocTally { yesVotes: number; noVotes: number; yesPop: number; noPop: number }

/** An ordinary bill: more votes AND more people for than against. */
export function passesDoubleMajority(t: BlocTally): boolean {
  return t.yesVotes > t.noVotes && t.yesPop > t.noPop;
}
/** War against the other civilization: the town rule's supermajority on the
 *  vote side (yes ≥ 2 × no, at least 3 blocs' worth) plus the people. */
export function passesWarMajority(t: BlocTally): boolean {
  return t.yesVotes >= 2 * t.noVotes && t.yesVotes >= 3 && t.yesPop > t.noPop;
}

// ── Bills ────────────────────────────────────────────────────────────────────

/** The only things the Assembly may legislate (the six powers). */
export const BILL_KINDS = ['levy', 'customs', 'relief', 'work', 'war', 'peace', 'drill_day', 'festival_day'] as const;
export type BillKind = (typeof BILL_KINDS)[number];
const BILL_KIND_SET: ReadonlySet<string> = new Set(BILL_KINDS);
export const isBillKind = (k: string): k is BillKind => BILL_KIND_SET.has(k);

export const LEVY_DEFAULT_BPS = 500;
/** THE 90/10 RULE IN COIN: never above a tenth of a settlement's inflow. */
export const LEVY_MAX_BPS = 1000;
export const CUSTOMS_DEFAULT_BPS = 500;
export const CUSTOMS_MAX_BPS = 1500;

/** Legal bounds of a bill's numeric value (the reducer clamps to these). */
export function billBounds(kind: BillKind): { min: number; max: number } {
  switch (kind) {
    case 'levy': return { min: 0, max: LEVY_MAX_BPS };
    case 'customs': return { min: 0, max: CUSTOMS_MAX_BPS };
    case 'relief': return { min: 0, max: 40 };          // units of grain released per needy member
    case 'work': return { min: 0, max: FEDERAL_WORKS.length - 1 };
    case 'drill_day': case 'festival_day': return { min: 0, max: 6 }; // a weekday, advisory
    default: return { min: 0, max: 1 };
  }
}
export function clampBill(kind: BillKind, value: number): number {
  const b = billBounds(kind);
  return Math.max(b.min, Math.min(b.max, Math.round(value)));
}

/** A failed bill goes once to MEDIATION: halfway to the opposing median. */
export function mediateValue(value: number, opposingMedian: number): number {
  return Math.round((value + opposingMedian) / 2);
}

export const FEDERAL_WORKS = [
  { key: 'road', label: 'a paved road between two cities', cost: 240, target: 360 },
  { key: 'reserve_granary', label: 'a reserve granary at the capital', cost: 180, target: 240 },
  { key: 'supply_line', label: 'a long-haul supply line to the capital', cost: 320, target: 480 },
  { key: 'beacon', label: 'a chain of road beacons', cost: 120, target: 180 },
] as const;

// ── Money ────────────────────────────────────────────────────────────────────

/** Coins a settlement owes on `inflow` coins of treasury income. */
export function levyDue(inflow: number, levyBps: number): number {
  if (inflow <= 0) return 0;
  return Math.floor(inflow * Math.max(0, Math.min(LEVY_MAX_BPS, levyBps)) / 10000);
}
/** Customs on a sale worth `value` coins crossing the civ border. */
export function customsDue(value: number, customsBps: number): number {
  if (value <= 0) return 0;
  return Math.floor(value * Math.max(0, Math.min(CUSTOMS_MAX_BPS, customsBps)) / 10000);
}
/** NO LEVY, NO VOTE: arrears above this many coins suspend the bloc. */
export const ARREARS_SUSPEND_COINS = 12;

/** Relief: who qualifies for grain from the federal reserve. */
export const RELIEF_SHORTAGE_STREAK = 3;
export const RELIEF_TREASURY_FLOOR = 60;
export function reliefEligible(shortageStreak: number, treasury: number): boolean {
  return shortageStreak >= RELIEF_SHORTAGE_STREAK && treasury < RELIEF_TREASURY_FLOOR;
}

/** Monthly-ish salaries per policy epoch, in coins (small on purpose). */
export const SALARY_SPEAKER = 6;
export const SALARY_MINISTER = 3;

// ── Offices ──────────────────────────────────────────────────────────────────

export const FEDERAL_OFFICES = ['speaker', 'chancellor', 'treasurer', 'marshal', 'justiciar', 'works', 'lore', 'censor'] as const;
export type FederalOffice = (typeof FEDERAL_OFFICES)[number];
export const FEDERAL_OFFICE_LABEL: Record<FederalOffice, string> = {
  speaker: 'Speaker of the Assembly', chancellor: 'Chancellor', treasurer: 'Treasurer of the Union',
  marshal: 'Marshal', justiciar: 'Justiciar', works: 'Master of Works', lore: 'Master of Lore', censor: 'Censor',
};

export interface OfficeCandidate { id: bigint; boldness: number; greed: number; diligence: number; sociability: number; age?: number }

/** Deterministic fitness of a candidate for a ministry (higher wins). */
export function ministerScore(office: FederalOffice, c: OfficeCandidate): number {
  switch (office) {
    case 'chancellor': return c.sociability * 1.2 + c.diligence * 0.3;
    case 'treasurer': return (100 - c.greed) * 1.0 + c.diligence * 0.8;
    case 'marshal': return c.boldness * 0.9 + c.diligence * 0.6;          // battle leadership's axes
    case 'justiciar': return (100 - c.boldness) * 0.7 + c.diligence * 0.9;
    case 'works': return c.diligence * 1.1 + (100 - c.greed) * 0.3;
    case 'lore': return c.diligence * 0.6 + c.sociability * 0.6;
    default: return c.sociability + c.diligence;
  }
}

/** The Speaker serves this many policy epochs (4 h each), then must sit out. */
export const SPEAKER_TERM_EPOCHS = 6;
export const SPEAKER_GAP_EPOCHS = 6;

// ── Stances: how a settlement's bloc leans, from its own interest ────────────

export interface MemberFacts {
  tier: string;
  treasury: number;
  shortageStreak: number;
  avgMood: number;
  /** coins of customs-liable trade this member ran last epoch (an exporter dislikes customs). */
  crossBorderTrade: number;
  /** metres to the nearest settlement of the OTHER civilization (a border town likes defence). */
  borderDistM: number;
  /** grievance this member holds against the other civilization's nearest town, 0..100+. */
  grievance: number;
  guards: number;
}

/** −100..+100: how much this member wants the bill. Positive votes yes. */
export function memberLean(kind: BillKind, value: number, current: number, f: MemberFacts): number {
  switch (kind) {
    case 'levy': {
      // Poor towns want the centre funded (they are net takers); rich ones resist a rise.
      const poor = f.treasury < 80 ? 1 : f.treasury > 300 ? -1 : 0;
      const dir = value > current ? 1 : value < current ? -1 : 0;
      return dir === 0 ? 0 : dir * (poor * 45 + (f.shortageStreak >= 2 ? 25 : 0) - (dir > 0 ? 10 : 0));
    }
    case 'customs': {
      const dir = value > current ? 1 : value < current ? -1 : 0;
      const exporter = f.crossBorderTrade > 20 ? -1 : 1;
      return dir * exporter * 40;
    }
    case 'relief': return (f.shortageStreak >= 1 ? 60 : 10) + (f.treasury > 300 ? -15 : 0);
    case 'work': return f.tier === 'hamlet' ? 5 : 35;
    case 'war': {
      const border = f.borderDistM < 60_000 ? 1 : 0;
      return f.grievance * 0.9 - 45 + border * 10 - (f.avgMood < 45 ? 25 : 0) - (f.guards < 2 ? 15 : 0);
    }
    case 'peace': return 40 - f.grievance * 0.4 + (f.shortageStreak >= 2 ? 25 : 0);
    default: return 20; // advisory standards are cheap to agree to
  }
}

/**
 * The bloc's final stance: the settlement's own lean plus its resident players'
 * preferences (each `FED_PLAYER_PREF_WEIGHT` ballots against `adults`), plus a
 * binding home-party mandate. Ties go against change.
 */
export function blocStance(lean: number, adults: number, playerAyes: number, playerNays: number, mandate: number): boolean {
  const npc = Math.max(1, adults) * (lean / 100);
  const players = (playerAyes - playerNays) * FED_PLAYER_PREF_WEIGHT;
  return npc + players + mandate > 0;
}

// ── The Tribunal ─────────────────────────────────────────────────────────────

/** Same-civ grievance at which the Justiciar offers a settlement (war bar is 80). */
export const TRIBUNAL_OPEN_AT = 60;
/** Cooling-off after an offer: no war vote between the two for this long. */
export const TRIBUNAL_COOL_MICROS = 4n * 3600n * 1_000_000n;
/** Grievance after an accepted settlement (what a peace resets it to). */
export const TRIBUNAL_SETTLED_GRIEVANCE = 20;
/** Award tiers in coins; the payer never owes more than a quarter of its chest. */
export const TRIBUNAL_AWARDS: readonly number[] = [0, 20, 50, 100];
export function tribunalAwardTier(grievance: number): number {
  return grievance >= 95 ? 3 : grievance >= 80 ? 2 : grievance >= TRIBUNAL_OPEN_AT ? 1 : 0;
}
export function tribunalAward(tier: number, payerTreasury: number): number {
  const base = TRIBUNAL_AWARDS[Math.max(0, Math.min(TRIBUNAL_AWARDS.length - 1, Math.floor(tier)))] ?? 0;
  return Math.max(0, Math.min(base, Math.floor(Math.max(0, payerTreasury) / 4)));
}
/** Goodwill a town forfeits with each same-civ neighbour when it refuses
 *  mediation and then opens the war. */
export const TRIBUNAL_DEFIANCE_GOODWILL = 10;

export interface MediationFacts { isPayer: boolean; award: number; treasury: number; avgMood: number; mayorBoldness: number; grievance: number }

/** Does this mayor accept the Justiciar's settlement? Towns stay free: a bold
 *  mayor nursing a white-hot grievance, or a payer who cannot afford it, says no. */
export function mayorAcceptsSettlement(f: MediationFacts): boolean {
  if (f.isPayer && f.award > f.treasury / 3) return false;
  const hawk = f.mayorBoldness > 70 && f.grievance >= 90;
  if (hawk) return false;
  if (!f.isPayer) return true; // being paid is easy to accept
  return f.avgMood >= 35 || f.mayorBoldness < 55;
}

// ── Contingents (requested, never commanded) ─────────────────────────────────

/** One soldier asked per this many households (the Swedish rote). */
export const HOUSEHOLDS_PER_SOLDIER = 5;
export function contingentAsk(households: number): number {
  return Math.max(0, Math.floor(households / HOUSEHOLDS_PER_SOLDIER));
}
export interface ContingentFacts { avgMood: number; treasury: number; grievance: number; borderDistM: number; guards: number }
/** How many of the asked does this mayor send? 0 is a legal answer. */
export function contingentSent(asked: number, f: ContingentFacts): number {
  if (asked <= 0 || f.guards < 1) return 0;
  let will = 0.35;
  if (f.avgMood >= 55) will += 0.2; else if (f.avgMood < 40) will -= 0.25;
  if (f.grievance >= 60) will += 0.25;
  if (f.borderDistM < 60_000) will += 0.2;
  if (f.treasury < 40) will -= 0.2;
  return Math.max(0, Math.min(asked, Math.round(asked * Math.max(0, Math.min(1, will)))));
}

// ── Legitimacy, promises, the audit ──────────────────────────────────────────

// ── The budget stance (research §3.9 `fed_budget`) ──────────────────────────
export const BUDGET_SPLITS = ['relief', 'works', 'defence'] as const;
export type BudgetSplit = typeof BUDGET_SPLITS[number];
export const isBudgetSplit = (s: string): s is BudgetSplit => (BUDGET_SPLITS as readonly string[]).includes(s);
export const BUDGET_LABEL: Record<BudgetSplit, string> = {
  relief: 'relief first — fill the reserve granary, build slowly',
  works: 'works first — build fast, buy little grain',
  defence: 'defence first — the war chest spends freely, the rest waits',
};
/** Coins a relief- or works-minded purse holds back from the war chest. */
export const BUDGET_CHEST_FLOOR = 40;
/**
 * What a split does to a sitting's DISCRETIONARY spending: the grain bought for
 * the reserve and a work's instalment step, each within ±50 % of the even
 * default (rule 2: one step), and the coins the war chest must leave in the
 * purse. SALARIES ARE NOT HERE: they are paid before any of this, so no split
 * can starve them. An unknown split is the even default.
 */
export function budgetShares(split: string): { reserveBuy: number; workStep: number; chestFloor: number } {
  switch (split) {
    case 'relief': return { reserveBuy: 1.5, workStep: 0.5, chestFloor: BUDGET_CHEST_FLOOR };
    case 'works': return { reserveBuy: 0.5, workStep: 1.5, chestFloor: BUDGET_CHEST_FLOOR };
    case 'defence': return { reserveBuy: 0.75, workStep: 0.5, chestFloor: 0 };
    default: return { reserveBuy: 1, workStep: 1, chestFloor: 0 };
  }
}
/** The rules' own pick — the split that answers the largest stress (the ask's fallback, option 0). */
export function budgetByStress(f: { needy: number; atWar: boolean; hottestGrievance: number; building: boolean }): BudgetSplit {
  if (f.atWar) return 'defence';
  if (f.needy > 0) return 'relief';
  if (f.hottestGrievance >= 70) return 'defence';
  return f.building ? 'works' : 'relief';
}

// ── The appointment tie-break (research §3.9 `appointment`) ─────────────────
/** Candidates within this many suitability points of the best are a TIE the Speaker may break. */
export const APPOINT_TIE_BAND = 12;
/**
 * The names a Speaker may choose between for one vacant chair: of the top THREE
 * by the deterministic score, those within the band of the best — and only when
 * at least two are. A clear winner is no question at all (returns []).
 * `rankedDesc` must already be sorted best-first.
 */
export function tiedCandidates<T extends { score: number }>(rankedDesc: readonly T[], band: number = APPOINT_TIE_BAND): T[] {
  if (rankedDesc.length < 2) return [];
  const best = rankedDesc[0].score;
  const tie = rankedDesc.slice(0, 3).filter((c) => best - c.score <= band);
  return tie.length >= 2 ? tie : [];
}

/** Below this share of the Assembly behind the Speaker, only relief passes. */
export const LEGITIMACY_FLOOR = 0.25;
/** Epochs a Speaker has to keep a promise before it counts as broken. */
export const PROMISE_DUE_EPOCHS = 4;

export interface MayorRecordFacts { treasuryStart: number; treasuryEnd: number; famineEpochs: number; lawsPassed: number; warsLost: number; awardsRefused: number }
export type AuditVerdict = 'commended' | 'passed' | 'censured';
/** The Censor's end-of-term audit (the Athenian euthyna) over `mayor_record`. */
export function auditVerdict(r: MayorRecordFacts): AuditVerdict {
  let score = 0;
  if (r.treasuryEnd >= r.treasuryStart) score += 1; else if (r.treasuryEnd < r.treasuryStart * 0.5) score -= 2;
  if (r.famineEpochs === 0) score += 1; else if (r.famineEpochs >= 3) score -= 2;
  if (r.lawsPassed >= 2) score += 1;
  score -= r.warsLost * 2 + r.awardsRefused;
  return score >= 2 ? 'commended' : score <= -2 ? 'censured' : 'passed';
}
